import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Users, Plus, Minus } from 'lucide-react';
import { supabase, Offer } from '../lib/supabase';
import { OfferCard } from '../components/OfferCard';
import { OfferRatings } from './Home';
import { DateRangePicker } from '../components/DateRangePicker';
import { SEO } from '../components/SEO';
import { Breadcrumb } from '../components/Breadcrumb';

interface CategoryPageProps {
  categorySlug: string;
}

export function CategoryPage({ categorySlug }: CategoryPageProps) {
  const navigate = useNavigate();
  const [allOffers, setAllOffers] = useState<Offer[]>([]);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [categoryName, setCategoryName] = useState('');
  const [loading, setLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [offerRatings, setOfferRatings] = useState<OfferRatings>({});
  const [currencyRates, setCurrencyRates] = useState<{ [key: string]: number }>({});
  const [searchParams, setSearchParams] = useState({
    checkIn: '',
    checkOut: '',
    guests: 1,
  });

  const getCategorySEO = () => {
    switch (categorySlug) {
      case 'appartements':
        return {
          title: 'Appartements à Louer à Oran',
          description: 'Découvrez notre sélection d\'appartements à louer à Oran. Locations meublées confortables pour vos vacances ou séjours d\'affaires.'
        };
      case 'villas':
        return {
          title: 'Villas de Luxe à Oran',
          description: 'Louez une villa de luxe à Oran. Propriétés haut de gamme avec piscine, jardin et équipements modernes pour un séjour exceptionnel.'
        };
      case 'logements-atypiques':
        return {
          title: 'Logements Atypiques à Oran',
          description: 'Découvrez des logements uniques et atypiques à Oran. Hébergements originaux pour une expérience inoubliable.'
        };
      case 'voitures':
        return {
          title: 'Location de Voitures à Oran',
          description: 'Location de voitures à Oran aux meilleurs prix. Large choix de véhicules récents pour tous vos déplacements.'
        };
      case 'scooters':
        return {
          title: 'Location de Scooters à Oran',
          description: 'Louez un scooter à Oran et déplacez-vous facilement en ville. Tarifs compétitifs et réservation simple.'
        };
      case 'activites':
        return {
          title: 'Activités et Excursions à Oran',
          description: 'Découvrez les meilleures activités et excursions à Oran. Visites guidées, sports nautiques et expériences uniques.'
        };
      default:
        return {
          title: `${categoryName} à Oran`,
          description: `Découvrez nos offres de ${categoryName} à Oran. Réservez en ligne facilement et profitez des meilleurs prix.`
        };
    }
  };

  useEffect(() => {
    loadOffers();
  }, [categorySlug]);

  const loadOffers = async () => {
    setLoading(true);

    const { data: category } = await supabase
      .from('categories')
      .select('*')
      .eq('slug', categorySlug)
      .maybeSingle();

    if (!category) {
      setLoading(false);
      return;
    }

    setCategoryName(category.name);

    const { data: offersData } = await supabase
      .from('offers')
      .select('*')
      .eq('category_id', category.id)
      .eq('is_available', true)
      .order('created_at', { ascending: false });

    if (offersData) {
      setAllOffers(offersData);
      setOffers(offersData);
    }
    setLoading(false);

    const [reviewsRes, ratesRes] = await Promise.all([
      supabase.from('reviews').select('offer_id, rating'),
      supabase.from('currency_rates').select('currency_code, rate'),
    ]);

    if (reviewsRes.data) {
      const ratings: OfferRatings = {};
      const grouped: { [id: string]: number[] } = {};
      reviewsRes.data.forEach((r: { offer_id: string; rating: number }) => {
        if (!grouped[r.offer_id]) grouped[r.offer_id] = [];
        grouped[r.offer_id].push(r.rating);
      });
      Object.entries(grouped).forEach(([offerId, ratingsList]) => {
        const avg = ratingsList.reduce((s, v) => s + v, 0) / ratingsList.length;
        ratings[offerId] = { average: Math.round(avg * 10) / 10, count: ratingsList.length };
      });
      setOfferRatings(ratings);
    }

    if (ratesRes.data) {
      const rates: { [key: string]: number } = {};
      ratesRes.data.forEach((r: { currency_code: string; rate: number }) => {
        rates[r.currency_code] = r.rate;
      });
      setCurrencyRates(rates);
    }
  };

  const handleSearch = async () => {
    setIsSearching(true);
    let filtered = [...allOffers];

    if (searchParams.guests > 1) {
      filtered = filtered.filter(offer => offer.capacity && offer.capacity >= searchParams.guests);
    }

    if (searchParams.checkIn && searchParams.checkOut) {
      const availableOfferIds = await checkAvailability(
        filtered.map(o => o.id),
        searchParams.checkIn,
        searchParams.checkOut
      );
      filtered = filtered.filter(offer => availableOfferIds.includes(offer.id));
    }

    setOffers(filtered);
    setIsSearching(false);
  };

  const checkAvailability = async (
    offerIds: string[],
    checkIn: string,
    checkOut: string
  ): Promise<string[]> => {
    const blockedOfferIds = new Set<string>();

    const { data: blockedDates } = await supabase
      .from('blocked_dates')
      .select('offer_id, blocked_date')
      .in('offer_id', offerIds)
      .gte('blocked_date', checkIn)
      .lt('blocked_date', checkOut);

    (blockedDates || []).forEach(d => blockedOfferIds.add(d.offer_id));

    const { data: reservations } = await supabase
      .from('reservations')
      .select('offer_id, start_date, end_date')
      .in('offer_id', offerIds)
      .eq('status', 'confirmed')
      .or(`and(start_date.lt.${checkOut},end_date.gt.${checkIn})`);

    (reservations || []).forEach(r => blockedOfferIds.add(r.offer_id));

    const { data: importedEvents } = await supabase
      .from('ical_imported_events')
      .select('offer_id, start_date, end_date')
      .in('offer_id', offerIds)
      .or(`and(start_date.lt.${checkOut},end_date.gt.${checkIn})`);

    (importedEvents || []).forEach(e => blockedOfferIds.add(e.offer_id));

    return offerIds.filter(id => !blockedOfferIds.has(id));
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 min-h-[80vh]">
        <div className="h-6 w-32 bg-gray-200 rounded animate-pulse mb-4" />
        <div className="h-4 w-48 bg-gray-200 rounded animate-pulse mb-6" />
        <div className="h-10 w-64 bg-gray-200 rounded animate-pulse mb-6" />
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="h-6 w-48 bg-gray-200 rounded animate-pulse mb-4" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="h-12 bg-gray-200 rounded-lg animate-pulse" />
            <div className="h-12 bg-gray-200 rounded-lg animate-pulse" />
            <div className="h-12 bg-gray-200 rounded-lg animate-pulse" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-white rounded-xl shadow-lg overflow-hidden animate-pulse">
              <div className="h-64 bg-gray-200" />
              <div className="p-4 space-y-3">
                <div className="h-5 bg-gray-200 rounded w-3/4" />
                <div className="h-4 bg-gray-200 rounded w-full" />
                <div className="flex justify-between items-center">
                  <div className="h-6 bg-gray-200 rounded w-24" />
                  <div className="h-9 bg-gray-200 rounded w-24" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const seoData = getCategorySEO();

  return (
    <div className="container mx-auto px-4 py-8">
      <SEO
        title={seoData.title}
        description={seoData.description}
      />
      <div className="mb-8">
        <button
          onClick={() => navigate('/')}
          className="text-blue-600 hover:text-blue-700 mb-4"
        >
          ← Retour à l'accueil
        </button>
        <Breadcrumb items={[{ label: categoryName }]} />
        <h1 className="text-4xl font-bold text-gray-800 mb-6">{categoryName}</h1>

        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Affiner votre recherche</h2>
          <div className="space-y-4 md:space-y-0 md:grid md:grid-cols-3 md:gap-4">
            <DateRangePicker
              startDate={searchParams.checkIn}
              endDate={searchParams.checkOut}
              onDateChange={(checkIn, checkOut) => setSearchParams({
                ...searchParams,
                checkIn,
                checkOut
              })}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Voyageurs
              </label>
              <div className="flex items-center bg-white border border-gray-300 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setSearchParams({
                    ...searchParams,
                    guests: Math.max(1, searchParams.guests - 1)
                  })}
                  className="p-3 hover:bg-gray-100 transition text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                  disabled={searchParams.guests <= 1}
                >
                  <Minus size={20} />
                </button>
                <div className="flex-1 flex items-center justify-center space-x-2 py-3 text-gray-800">
                  <Users size={20} className="text-gray-400" />
                  <span className="font-medium text-lg">{searchParams.guests}</span>
                </div>
                <button
                  type="button"
                  onClick={() => setSearchParams({
                    ...searchParams,
                    guests: Math.min(20, searchParams.guests + 1)
                  })}
                  className="p-3 hover:bg-gray-100 transition text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                  disabled={searchParams.guests >= 20}
                >
                  <Plus size={20} />
                </button>
              </div>
            </div>

            <div className="md:flex md:items-end">
              <button
                onClick={handleSearch}
                disabled={isSearching}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 transition transform hover:scale-[1.02] flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none min-h-[44px]"
              >
                {isSearching ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Recherche...</span>
                  </>
                ) : (
                  <>
                    <Search size={20} />
                    <span>Rechercher</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        <p className="text-gray-600">{offers.length} offre(s) disponible(s)</p>
      </div>

      {offers.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-xl text-gray-600">Aucune offre disponible pour le moment</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {offers.map((offer, index) => (
            <OfferCard
              key={offer.id}
              offer={offer}
              rating={offerRatings[offer.id]}
              currencyRates={currencyRates}
              priority={index < 3}
              searchParams={{
                checkIn: searchParams.checkIn,
                checkOut: searchParams.checkOut,
                guests: searchParams.guests
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
